import React from "react";
import styled from "styled-components";
import { click as clickSound } from "../../sound/sound";

// The scripted mouse for the first-visit boot scene: a classic white
// arrow that glides between DOM targets and "clicks" them. The real
// cursor is hidden by the caller while this plays.
//
// steps: [{ target: "<data-scene-target value>" | {x,y},
//           click?: true,       // play click sound + fire onArrive
//           travelMs?, pauseMs? }]
// onStep(index) fires when a step's movement finishes (before pauseMs);
// use it to open menus at the right beat. onDone fires after the last
// pause.

const DEFAULT_TRAVEL = 900;
const DEFAULT_PAUSE = 350;

// #root carries `zoom: var(--ui-zoom)`; getBoundingClientRect reports
// visual (zoomed) px while our fixed-position coordinates live inside the
// zoomed space — divide to convert.
function readUiZoom() {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(
    "--ui-zoom",
  );
  const n = parseFloat(raw);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

function resolveTarget(target) {
  // Function targets resolve at step START — for aiming at things only
  // known then (e.g. wherever the player's real mouse currently is).
  if (typeof target === "function") target = target();
  if (target == null) return null;
  if (typeof target === "object") return target;
  const el = document.querySelector(`[data-scene-target="${target}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  const zoom = readUiZoom();
  // Aim slightly inside the element, like a person would.
  return {
    x: (r.left + r.width * 0.5) / zoom,
    y: (r.top + r.height * 0.55) / zoom,
  };
}

// Cubic ease-in-out with a whisper of overshoot on arrival — human-ish,
// not robotic-linear.
function ease(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

// The ghost can't fire real mouseenter events, but menus should still
// react as it passes: hit-test the arrow tip each frame and stamp
// data-ghost-hover on whichever menuitem is underneath (styles in
// StartMenu/MenuBar treat it as hover).
let ghostHoverEl = null;
function updateGhostHover(x, y, zoom) {
  // elementsFromPoint (plural): the scene's invisible click-to-skip
  // blocker sits on top and would otherwise swallow every hit-test —
  // walk the stack for the first real menu item under the arrow.
  const el = document
    .elementsFromPoint(x * zoom, y * zoom)
    .map((e) => e.closest?.('[role="menuitem"]'))
    .find(Boolean);
  if (el === ghostHoverEl) return;
  if (ghostHoverEl) ghostHoverEl.removeAttribute("data-ghost-hover");
  ghostHoverEl = el ?? null;
  if (ghostHoverEl) ghostHoverEl.setAttribute("data-ghost-hover", "true");
}
function clearGhostHover() {
  if (ghostHoverEl) ghostHoverEl.removeAttribute("data-ghost-hover");
  ghostHoverEl = null;
}

function GhostCursor({ steps, onStep, onDone, startAt }) {
  const [pos, setPos] = React.useState(startAt ?? { x: 0, y: 0 });
  const [pressed, setPressed] = React.useState(false);
  const posRef = React.useRef(pos);
  posRef.current = pos;
  const callbacksRef = React.useRef({ onStep, onDone });
  callbacksRef.current = { onStep, onDone };

  React.useEffect(() => {
    let cancelled = false;
    let raf = null;
    let timeout = null;

    const runStep = (i) => {
      if (cancelled) return;
      if (i >= steps.length) {
        if (callbacksRef.current.onDone) callbacksRef.current.onDone();
        return;
      }
      const step = steps[i];
      const to = resolveTarget(step.target);
      if (!to) {
        // Target not in the DOM (layout changed?) — skip the beat rather
        // than strand the scene.
        runStep(i + 1);
        return;
      }
      const from = { ...posRef.current };
      const travel = step.travelMs ?? DEFAULT_TRAVEL;
      const t0 = performance.now();

      const frame = (now) => {
        if (cancelled) return;
        const t = Math.min(1, (now - t0) / travel);
        const k = ease(t);
        const x = from.x + (to.x - from.x) * k;
        const y = from.y + (to.y - from.y) * k;
        setPos({ x, y });
        updateGhostHover(x, y, readUiZoom());
        if (t < 1) {
          raf = requestAnimationFrame(frame);
        } else {
          if (step.click) {
            clickSound();
            setPressed(true);
            setTimeout(() => !cancelled && setPressed(false), 120);
          }
          if (callbacksRef.current.onStep) callbacksRef.current.onStep(i);
          timeout = setTimeout(() => runStep(i + 1), step.pauseMs ?? DEFAULT_PAUSE);
        }
      };
      raf = requestAnimationFrame(frame);
    };

    runStep(0);
    return () => {
      cancelled = true;
      clearGhostHover();
      if (raf != null) cancelAnimationFrame(raf);
      if (timeout != null) clearTimeout(timeout);
    };
    // The scene runs once per mount, driven by the steps it mounted with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Arrow
      style={{ "--x": pos.x + "px", "--y": pos.y + "px" }}
      data-pressed={pressed ? "true" : undefined}
      viewBox="0 0 12 19"
      aria-hidden="true"
    >
      <path
        d="M1 1 L1 15 L4.5 12 L7 18 L9.5 17 L7 11 L11 11 Z"
        fill="#fff"
        stroke="#000"
        strokeWidth="1"
      />
    </Arrow>
  );
}

const Arrow = styled.svg`
  position: fixed;
  left: 0;
  top: 0;
  width: 17px;
  height: 27px;
  transform: translate(var(--x), var(--y));
  z-index: 5000;
  pointer-events: none;
  filter: drop-shadow(1px 1px 1px rgba(0, 0, 0, 0.5));

  &[data-pressed="true"] {
    transform: translate(var(--x), var(--y)) scale(0.92);
  }
`;

export default GhostCursor;
